// Chat Agents Main Listing Page
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Pencil,
  Trash2,
  PlusCircle,
  MessageSquare,
  Settings,
  Play,
  Pause,
  Eye,
  Copy,
  RefreshCw,
  UserCog,
  ExternalLink
} from 'lucide-react';
import chatAgentService from './ChatIntents/chatAgentService';
import PermissionsAssignmentModal from '../../common/Permissions/PermissionsAssignmentModal';

// Type definitions matching your API response
interface ChatAgent {
  id: string;
  agent_id: string;
  tenant_id: string;
  agent_title: string;
  agent_type: string;
  chat_type: string;
  llm_name: string;
  agent_icon?: string;
  created_at?: string;
  updated_at?: string;
}

const ChatAgents: React.FC = () => {
  const navigate = useNavigate();
  const [chatAgents, setChatAgents] = useState<ChatAgent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showAssignmentModal, setAssignmentModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<ChatAgent | null>(null);

  // Fetch chat agents from API
  const fetchChatAgents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await chatAgentService.getAgentList();
      
      console.log('API Response:', response);
      
      if (response?.success && response?.data) {
        setChatAgents(response.data);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to fetch chat agents';
      setError(errorMessage);
      console.error('Error fetching chat agents:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch agents on component mount
  useEffect(() => {
    fetchChatAgents();
  }, []);

  const handleDeleteAgent = async (id: string): Promise<void> => {
    if (!window.confirm('Are you sure you want to delete this chat agent?')) {
      return;
    }

    try {
      const payload = { id: id, entity_type: 'agent' };
      await chatAgentService.deleteEntity(payload);
      setChatAgents(prev => prev.filter(agent => agent.id !== id));
    } catch (err: any) {
      console.error('Error deleting agent:', err);
      alert('Failed to delete agent');
    }
  };

  const handleCopyAgentId = (agentId: string): void => {
    navigator.clipboard.writeText(agentId);
    alert('Agent ID copied to clipboard');
  };

  const handleManagePermissions = (agent: ChatAgent): void => {
    setSelectedAgent(agent);
    setAssignmentModal(true);
  };

  return (
    <div>
      {/* Header + Button */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-white">Chat Agents</h2>
          <p className="text-sm text-gray-400">Create and manage intelligent chat agents with custom behaviors and integrations.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchChatAgents}
            className="flex items-center bg-neutral-700 hover:bg-neutral-600 text-white px-4 py-2 rounded-md text-sm shadow"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <a
            href='/portal/createchatagent'
            className="flex items-center bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm shadow"
          >
            <PlusCircle className="w-4 h-4 mr-2" /> Create New Chat Agent
          </a>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-900/50 border border-red-700 rounded-lg p-4">
          <p className="text-red-200 text-sm">
            <strong>Error:</strong> {error}
          </p>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <RefreshCw className="w-8 h-8 text-indigo-500 animate-spin" />
          <span className="ml-3 text-gray-400">Loading chat agents...</span>
        </div>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Total Agents</p>
                  <p className="text-2xl font-semibold text-white">{chatAgents.length}</p>
                </div>
                <MessageSquare className="w-8 h-8 text-indigo-500" />
              </div>
            </div>

            <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Basic Agents</p>
                  <p className="text-2xl font-semibold text-green-400">
                    {chatAgents.filter(a => a.agent_type?.toLowerCase() === 'basic').length}
                  </p>
                </div>
                <Play className="w-8 h-8 text-green-500" />
              </div>
            </div>

            <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Advanced Agents</p>
                  <p className="text-2xl font-semibold text-blue-400">
                    {chatAgents.filter(a => a.agent_type?.toLowerCase() === 'advanced').length}
                  </p>
                </div>
                <Settings className="w-8 h-8 text-blue-500" />
              </div>
            </div>

            <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">With LLM</p>
                  <p className="text-2xl font-semibold text-purple-400">
                    {chatAgents.filter(a => a.llm_name && a.llm_name !== 'N/A').length}
                  </p>
                </div>
                <MessageSquare className="w-8 h-8 text-purple-500" />
              </div>
            </div>
          </div>

          {/* Empty State */}
          {chatAgents.length === 0 ? (
            <div className="border border-neutral-800 rounded-lg p-12 text-center">
              <MessageSquare className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No Chat Agents Found</h3>
              <p className="text-gray-400 mb-6">Get started by creating your first chat agent</p>
              <a
                href='/portal/createchatagent'
                className="inline-flex items-center bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-md text-sm shadow"
              >
                <PlusCircle className="w-4 h-4 mr-2" /> Create New Chat Agent
              </a>
            </div>
          ) : (
            /* Table */
            <div className="overflow-x-auto border border-neutral-800 rounded-lg">
              <table className="min-w-full text-sm text-left text-gray-300">
                <thead className="bg-neutral-900 text-gray-400">
                  <tr>
                    <th className="px-4 py-3">Agent Name</th>
                    <th className="px-4 py-3">Agent ID</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Chat Type</th>
                    <th className="px-4 py-3">LLM Model</th>
                    <th className="px-4 py-3">URL</th>
                    <th className="px-4 py-3">Created</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {chatAgents.map((agent) => (
                    <tr key={agent.id} className="border-t border-neutral-800 hover:bg-neutral-800/30">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center overflow-hidden">
                            {agent.agent_icon ? (
                              <img 
                                src={agent.agent_icon} 
                                alt={agent.agent_title}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  // Hide image and show default icon if image fails to load
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            ) : null}
                            {!agent.agent_icon && (
                              <MessageSquare className="w-4 h-4 text-white" />
                            )}
                          </div>
                          <span className="font-medium text-white">{agent.agent_title}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs text-gray-400">{agent.agent_id}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                          agent.agent_type?.toLowerCase() === 'advanced'
                            ? 'bg-purple-700 text-purple-100'
                            : 'bg-blue-700 text-blue-100'
                        }`}>
                          {agent.agent_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="bg-neutral-800 px-2 py-1 rounded text-xs capitalize">{agent.chat_type}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          agent.llm_name === 'N/A' 
                            ? 'bg-red-700 text-red-100' 
                            : 'bg-green-700 text-green-100'
                        }`}>
                          {agent.llm_name}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-gray-400 truncate max-w-[220px]">
                            {`https://chat.selficai.com/enterprisechat/${agent.agent_id}`}
                          </span>
                          <a
                            href={`https://chat.selficai.com/enterprisechat/${agent.agent_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-400 hover:text-indigo-300 transition-colors"
                            title="Open chat URL"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {agent.created_at 
                          ? new Date(agent.created_at).toLocaleDateString()
                          : 'N/A'
                        }
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2"> 

                          <button 
                            className="text-blue-400 hover:text-blue-200" 
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => handleCopyAgentId(agent.agent_id)}
                            className="text-gray-400 hover:text-gray-200"
                            title="Copy Agent ID"
                          >
                            <Copy className="w-4 h-4" />
                          </button>

                          <button 
                            className="text-yellow-400 hover:text-yellow-200" 
                            title="Configure"
                          >
                            <Settings className="w-4 h-4" />
                          </button>

                          <button 
                            onClick={() => navigate(`/portal/chatbot/${agent.id}`)}
                            className="text-orange-400 hover:text-orange-200" 
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => handleDeleteAgent(agent.id)}
                            className="text-red-400 hover:text-red-200"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showAssignmentModal && selectedAgent && (
        <PermissionsAssignmentModal
          onClose={() => {
            setAssignmentModal(false);
            setSelectedAgent(null);
          }}
          onSave={() => {
            setAssignmentModal(false);
            setSelectedAgent(null);
          }}
          entityType="chat-agent"
          entityName={selectedAgent.agent_title}
        />
      )}
    </div>
  );
};

export default ChatAgents;
 